/**
 * Home Page - Entry point with navigation options
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Container,
  ThemeIcon,
} from "@mantine/core";
import {
  IconRobot,
  IconSettings,
  IconShield,
  IconArrowRight,
  IconLayoutGrid,
} from "@tabler/icons-react";
import { apiClient } from "../api/client";
import { usePageView } from "../hooks/usePageView";

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
}

function FeatureCard({ icon, title, description, onClick }: FeatureCardProps) {
  return (
    <Card
      shadow="sm"
      padding="lg"
      radius="md"
      withBorder
      style={{
        cursor: "pointer",
        transition: "transform 0.2s, box-shadow 0.2s",
      }}
      onClick={onClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-4px)";
        e.currentTarget.style.boxShadow = "var(--mantine-shadow-md)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "var(--mantine-shadow-sm)";
      }}
    >
      <Group justify="space-between" align="flex-start">
        <Group>
          <ThemeIcon size="xl" radius="md" variant="light">
            {icon}
          </ThemeIcon>
          <Stack gap={4}>
            <Text fw={600} size="lg">
              {title}
            </Text>
            <Text size="sm" c="dimmed">
              {description}
            </Text>
          </Stack>
        </Group>
        <IconArrowRight size={20} style={{ color: "var(--mantine-color-gray-5)" }} />
      </Group>
    </Card>
  );
}

export function HomePage() {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);

  usePageView();

  useEffect(() => {
    apiClient
      .getCurrentUser()
      .then((user) => setIsAdmin(user.is_admin))
      .catch(() => setIsAdmin(false));
  }, []);

  const handleStartBuilder = () => {
    navigate("/builder");
  };

  const handleOpenConfigure = () => {
    navigate("/configure");
  };

  return (
    <Box
      style={{
        // Own scrolling, since #root is overflow:hidden for the builder.
        width: "100%",
        height: "100%",
        overflowY: "auto",
        display: "flex",
        backgroundColor: "var(--mantine-color-gray-0)",
      }}
    >
      {/* margin auto rather than align-items: center — centring a flex child
          that is taller than its container clips the top off with no way to
          scroll back to it, which is what a phone screen produces here. */}
      <Container size="sm" my="xl" style={{ margin: "auto" }}>
        <Stack gap="xl">
          <Box ta="center">
            <Title order={1} mb="xs">
              Robot Compiler
            </Title>
            <Text size="lg" c="dimmed">
              Build and configure your robot URDF
            </Text>
          </Box>

          <Stack gap="md">
            <FeatureCard
              icon={<IconRobot size={24} />}
              title="URDF Builder"
              description="Create robot URDFs by assembling modular components"
              onClick={handleStartBuilder}
            />

            <FeatureCard
              icon={<IconSettings size={24} />}
              title="Configuration Generator"
              description="Generate MoveIt2, Gazebo, and Imitation Learning configs from URDF"
              onClick={handleOpenConfigure}
            />

            <FeatureCard
              icon={<IconLayoutGrid size={24} />}
              title="Assembly Catalog"
              description="Browse assemblies published by users and open them in the builder"
              onClick={() => navigate("/catalog")}
            />

            {isAdmin && (
              <FeatureCard
                icon={<IconShield size={24} />}
                title="Admin Console"
                description="Review access history and the robots people exported"
                onClick={() => navigate("/admin")}
              />
            )}
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}
